import type { ErrorEvent, StackFrame } from '@sentry/react'

/*
  Browser extensions (wallets such as Backpack, password managers, …) inject scripts into the page
  world. When those scripts throw — e.g. a string-eval refused by our `script-src 'self'` CSP, which
  is the intended behaviour — the rejection reaches our global handlers and Sentry files it as an
  admin bug even though no admin code is on the stack.

  An event is treated as third-party only when it has a stack trace and every frame (ignoring
  Sentry's own wrapper frames) is clearly not ours: an `<anonymous>` eval'd/injected frame or an
  extension URL. Events without a stack, or with any frame we cannot positively identify as
  third-party, are kept — better a report we did not need than a bug we never saw.
*/

const ANONYMOUS_FILENAME = '<anonymous>'
// chrome-extension://, moz-extension://, safari-extension://, safari-web-extension://, ms-browser-extension://
const EXTENSION_URL = /^[a-z-]*extension:\/\//i
// Sentry wraps event listeners/timers in `sentryWrapped`; that frame lives in our bundle but is not
// evidence that our code was involved.
const SENTRY_WRAPPER_FUNCTION = /(?:^|\.)sentryWrapped$/

const isSentryWrapperFrame = (frame: StackFrame) =>
  typeof frame.function === 'string' && SENTRY_WRAPPER_FUNCTION.test(frame.function)

const isThirdPartyFrame = (frame: StackFrame) => {
  const filename = frame.filename ?? frame.abs_path
  if (typeof filename !== 'string') return false
  return filename === ANONYMOUS_FILENAME || EXTENSION_URL.test(filename)
}

/**
 * True when the event's stack consists exclusively of third-party (anonymous or browser-extension)
 * frames, so it cannot originate from the admin application.
 */
export const isThirdPartyOnlyEvent = (event: Pick<ErrorEvent, 'exception'>): boolean => {
  const frames = (event.exception?.values ?? [])
    .flatMap((value) => value.stacktrace?.frames ?? [])
    .filter((frame) => !isSentryWrapperFrame(frame))

  if (frames.length === 0) return false
  return frames.every(isThirdPartyFrame)
}
